import { Check, NavArrowDown } from 'iconoir-react'
import { useState, type ReactElement } from 'react'
import Companies from 'app/components/Icon/CompaniesIcon'
import { Button } from 'app/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { OptionType } from 'types/OptionType'

/**
 * Picks the one location a page is about, the way the Inbox does: a searchable list, one line per
 * name, so practices with many long location names can still find theirs.
 */
export function AgentLocationPicker({
  options,
  value,
  onChange,
}: {
  options: OptionType[]
  value?: string
  onChange: (value: string) => void
}): ReactElement {
  const [open, setOpen] = useState(false)
  const selected = options.find((option) => option.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="tertiary"
          size="sm"
          aria-label="Location"
          aria-expanded={open}
          className="max-w-72"
          data-testid="agent-location-picker"
        >
          <Companies width={16} height={16} className="shrink-0 text-subtle" />
          <span className="min-w-0 truncate text-ui-xs">{selected?.name ?? `Select a location`}</span>
          <NavArrowDown className="h-3.5 w-3.5 shrink-0 text-minimal" strokeWidth={2} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-80 p-0" data-testid="agent-location-picker-menu">
        <Command>
          <CommandInput placeholder="Search locations…" data-testid="agent-location-picker-search" />
          <CommandList>
            <CommandEmpty>No locations found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  data-testid={`agent-location-option-${option.value}`}
                  value={option.name ?? option.value!}
                  onSelect={() => {
                    onChange(option.value!)
                    setOpen(false)
                  }}
                >
                  <span className="min-w-0 flex-1 truncate" title={option.name}>
                    {option.name}
                  </span>
                  {option.value === value && <Check className="ml-2 h-4 w-4 shrink-0 text-default" strokeWidth={2} />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
